import React, { useEffect, useMemo, useReducer, useState } from 'react';
import {
    Button,
    Col,
    Dropdown,
    Form,
    Row,
    Card,
    Spinner,
    Alert,
    ListGroup,
    InputGroup,
} from 'react-bootstrap';

// Assuming these are correctly path-resolved in your project
import { createText } from '../../util/cardEffects/cardText.ts'; // Ensure this path is correct
import { EffectSchema } from '../../util/cardEffects/validator.ts'; // Ensure this path is correct
import { Effect, SCHOOL_COLORS, SCHOOL_NAMES } from '../../util/cardEffects/effectTypes.ts'; // Ensure this path is correct
import type { School } from '../../util/cardEffects/effectTypes.ts'; // Ensure this path is correct
import { PlayingCard } from '../../paths/games/cardComponent.tsx';
import CardAbilitiesEditor from './AbilityEditor.tsx';
import { isEffectType } from '../../util/cardEffects/effectProcessing.tsx';
import { api } from '../../index.tsx';
import { ExpandToFitContainer } from '../decks/ExpandToFitContainer.tsx';

// --- Type Definitions ---
// Re-defining CardData with string types for form fields, and null/Effect for parsed abilities
type CardDataForm = {
    card_id: string | null; // Can be string for new, null for empty, number when fetched
    class_card: boolean;
    energize_amount: number
    school: School;
    name: string;
    cost: number; // Keep as string for form input
    cast_threshold: number; // Keep as string for form input
    play_ability: string; // JSON string representation
    cast_ability: string;
    sustain_ability: string;
    passive_ability: string;
    max_count: number;
    set: string;
};

export const ABILITY_FIELDS: Array<keyof CardDataForm> = [
    'play_ability',
    'cast_ability',
    'sustain_ability',
    'passive_ability',
];

// Type for card list items (from API, or display)
type CardListItem = {
    card_id: number;
    class_card: boolean;
    energize_amount: number;
    school: School;
    name: string;
    cost: number;
    play_ability: Effect;
    cast_ability: Effect;
    sustain_ability: Effect;
    passive_ability: Effect;
    cast_threshold: number;
    max_count: number;
    set: string;
};

// --- Helper Functions ---
// Parses a JSON string to an Effect object, or returns null if invalid/empty
function parseEffect(jsonStr: string): Effect | null {
    if (jsonStr === null || jsonStr.trim() === '' || jsonStr.trim() === 'null') {
        return null;
    }
    try {
        const raw = JSON.parse(jsonStr);
        return EffectSchema.parse(raw);
    } catch (e) {
        console.error('Failed to parse effect JSON:', e, jsonStr);
        return null; // Return null on parse error
    }
}

// Reducer for focusedCardData
const focusedCardDataReducer = (state: CardDataForm, action: Partial<CardDataForm>) => {
    return { ...state, ...action };
};

export default function CardManagementPanel() {
    // --- Constants ---
    const blankCardData: CardDataForm = useMemo(
        () => ({
            card_id: null,
            class_card: false,
            energize_amount: 0,
            school: 'Manifest',
            name: 'Card Name',
            cost: 0,
            cast_threshold: 0,
            play_ability: '',
            cast_ability: '',
            sustain_ability: '',
            passive_ability: '',
            max_count: 4,
            set: 'Base',
        }),
        [],
    );

    // --- State ---
    const [cardList, setCardList] = useState<CardListItem[]>([]);
    const [newCardName, setNewCardName] = useState('');
    const [focusedCardId, setFocusedCardId] = useState<number | null>(null); // Use number for IDs
    const [searchTerm, setSearchTerm] = useState('');
    const [searchClass, setSearchClass] = useState(false);
    const [searchRitual, setSearchRitual] = useState(false);
    const [searchCost, setSearchCost] = useState('');
    const [searchSchool, setSearchSchool] = useState<string | null>(null);

    const [focusedCardData, dispatchFocusedCardData] = useReducer(
        focusedCardDataReducer,
        blankCardData,
    );

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false); // For update/delete/create operations

    // --- API Calls ---
    const fetchCardList = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.post('/cards/get', {}, { withCredentials: true });
            // Sort immediately for display
            setCardList(
                res.data.sort((a: CardListItem, b: CardListItem) =>
                    a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
                ),
            );
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to fetch card list.');
        } finally {
            setLoading(false);
        }
    };

    const fetchFocusedCard = async (id: number) => {
        setSaving(true); // Treat fetching single card as an 'updating' state
        setError(null);
        try {
            const res = await api.post('/cards/get', { cardId: id }, { withCredentials: true });
            const rawCardData = res.data;

            // Prepare data for form state (stringify JSON fields, convert numbers to string)
            const preparedData: Partial<CardDataForm> = {
                ...rawCardData,
                card_id: Number(rawCardData.card_id),
                cost: Number(rawCardData.cost),
                class_card: Boolean(rawCardData.class_card === true),
                energize_amount: Number(rawCardData.energize_amount),
                cast_threshold: Number(rawCardData.cast_threshold),
            };
            ABILITY_FIELDS.forEach((field) => {
                if (preparedData[field] !== undefined && field !== "school" && field !== "class_card" && field !== "energize_amount" && field !== "max_count" && field !== "cost" && field !== "cast_threshold") {
                    preparedData[field] = JSON.stringify(rawCardData[field] || null, null, 2); // Pretty print JSON
                }
            });
            dispatchFocusedCardData(preparedData);
        } catch (e: any) {
            setError(e.response?.data?.message || `Failed to load card ID: ${id}.`);
            setFocusedCardId(null); // Clear focus if load fails
        } finally {
            setSaving(false);
        }
    };

    const createCard = async () => {
        if (!newCardName.trim()) {
            setError('Card name cannot be empty.');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const res = await api.post('/cards/add', { cardname: newCardName }, { withCredentials: true });
            setNewCardName(''); // Clear input
            await fetchCardList(); // Refresh list
            setFocusedCardId(res.data.cardId); // Focus on the new card
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to create card.');
        } finally {
            setSaving(false);
        }
    };

    const updateCard = async () => {
        if (focusedCardId === null) {
            setError('No card selected to update.');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            // Prepare data for backend: parse numbers, parse JSON abilities
            type CardData = {
                card_id: number;
                name: string;
                class_card: boolean;
                energize_amount: number;
                school: string;
                cost: number;
                cast_threshold: number;
                cast_ability: ReturnType<typeof parseEffect> | null;
                play_ability: ReturnType<typeof parseEffect> | null;
                passive_ability: ReturnType<typeof parseEffect> | null;
                sustain_ability: ReturnType<typeof parseEffect> | null;
                max_count: number;
                set: string;
            };

            const dataToSend: CardData = {
                card_id: Number(focusedCardData.card_id),
                name: focusedCardData.name,
                class_card: focusedCardData.class_card,
                energize_amount: focusedCardData.energize_amount,
                school: focusedCardData.school,
                cost: focusedCardData.cost,
                cast_threshold: focusedCardData.cast_threshold,
                cast_ability: parseEffect(focusedCardData.cast_ability as string),
                play_ability: parseEffect(focusedCardData.play_ability as string),
                passive_ability: parseEffect(focusedCardData.passive_ability as string),
                sustain_ability: parseEffect(focusedCardData.sustain_ability as string),
                max_count: focusedCardData.max_count,
                set: focusedCardData.set,
            };

            await api.post('/cards/update', { cardData: dataToSend }, { withCredentials: true });
            await fetchCardList(); // Refresh list to reflect changes
            // Re-fetch focused card to ensure form reflects the saved backend state,
            // particularly if parsing or backend logic changes anything.
            await fetchFocusedCard(focusedCardId);
            setError('Card updated successfully!'); // Success message
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to update card.');
        } finally {
            setSaving(false);
        }
    };

    const deleteCard = async () => {
        if (focusedCardId === null) {
            setError('No card selected to delete.');
            return;
        }
        if (!window.confirm(`Are you sure you want to delete card ID ${focusedCardId} (${focusedCardData.name})?`)) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await api.post('/cards/delete', { cardId: focusedCardId }, { withCredentials: true });
            setFocusedCardId(null); // Clear focus
            dispatchFocusedCardData(blankCardData); // Clear form
            await fetchCardList(); // Refresh list
            setError('Card deleted successfully!'); // Success message
        } catch (e: any) {
            setError(e.response?.data?.message || 'Failed to delete card.');
        } finally {
            setSaving(false);
        }
    };

    // --- Effects ---
    // Effect to fetch initial card list on mount
    useEffect(() => {
        fetchCardList();
    }, []);

    // Effect to fetch details when focusedCardId changes
    useEffect(() => {
        if (focusedCardId !== null) {
            fetchFocusedCard(focusedCardId);
        } else {
            // If focusedCardId becomes null, clear the form
            dispatchFocusedCardData(blankCardData);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedCardId]); // blankCardData in dependency array for useReducer initial state is not needed

    // --- Filtered Card List for Display ---
    const filteredCardList = useMemo(() => {
        return cardList.filter((card) => {
            const matchesNameOrId =
                card.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                String(card.card_id) === searchTerm;
            const matchesSchool = searchSchool === null || card.school === searchSchool;
            const matchesCost =
                searchCost === '' || String(card.cost) === searchCost;
            const matchesClass = (card.class_card !== null && card.class_card !== false) === searchClass
            const matchesRitual = (isEffectType("ritual", card.play_ability ? (card.play_ability) : null)
                || isEffectType("ritual", card.cast_ability ? (card.cast_ability) : null)
                || isEffectType("ritual", card.sustain_ability ? (card.sustain_ability) : null)
                || isEffectType("ritual", card.passive_ability ? (card.passive_ability) : null)) === searchRitual
            return matchesNameOrId && matchesSchool && matchesCost && matchesClass && matchesRitual;
        }).sort((a, b) => a.school > b.school ? 1 : -1);
    }, [cardList, searchTerm, searchCost, searchSchool, searchClass, searchRitual]);

    // for card select panel
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Calculate paginated data
    const totalPages = Math.ceil(filteredCardList.length / itemsPerPage);
    const paginatedCards = filteredCardList.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredCardList.length]);

    // --- Render ---
    return (
        <Card className='shadow-sm mb-4'>
            <Card.Header as='h4'>Card Management</Card.Header>
            <Card.Body>
                {error && <Alert variant='danger'>{error}</Alert>}
                {loading && (
                    <div className='text-center my-3'>
                        <Spinner animation='border' role='status' className='me-2' />
                        <p>Loading card data...</p>
                    </div>
                )}

                <Row className='mb-4 g-3'>
                    {/* Create New Card */}
                    <Col md={6} lg={4}>
                        <Card className='h-100 shadow-sm'>
                            <Card.Body>
                                <Card.Title className='mb-3'>Create New Card</Card.Title>
                                <Form onSubmit={(e) => { e.preventDefault(); createCard(); }}>
                                    <InputGroup>
                                        <Form.Control
                                            type='text'
                                            placeholder='New card name'
                                            value={newCardName}
                                            onChange={(e) => setNewCardName(e.target.value)}
                                            disabled={saving}
                                        />
                                        <Button
                                            variant='success'
                                            type='submit'
                                            disabled={!newCardName.trim() || saving}
                                        >
                                            Create
                                        </Button>
                                    </InputGroup>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Card List Search & Filter */}
                    <Col md={6} lg={8}>
                        <Card className='h-100 shadow-sm'>
                            <Card.Body>
                                <Card.Title className='mb-3'>Search Cards</Card.Title>
                                <Row className='g-2'>
                                    <Col md={3}>
                                        <Form.Control
                                            placeholder='Search by name or ID'
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Control
                                            type='number'
                                            placeholder='Cost'
                                            value={searchCost}
                                            onChange={(e) => setSearchCost(e.target.value)}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Dropdown onSelect={(val) => setSearchSchool(val)}>
                                            <Dropdown.Toggle variant='outline-secondary' className='w-100'>
                                                {searchSchool || 'All Schools'}
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className='w-100'>
                                                <Dropdown.Item eventKey={undefined}>All</Dropdown.Item>
                                                {SCHOOL_NAMES.map((school) => (
                                                    <Dropdown.Item key={school} eventKey={school}>
                                                        {school}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </Col>
                                    <Col md={1}>
                                        <Form.Check
                                            label={"class"}
                                            style={{ marginTop: "10px" }}
                                            checked={searchClass}
                                            onChange={(e) => {
                                                setSearchClass(!searchClass);
                                            }}
                                            disabled={saving}
                                        />
                                    </Col>
                                    <Col md={1}>
                                        <Form.Check
                                            label={"ritual"}
                                            style={{ marginTop: "10px" }}
                                            checked={searchRitual}
                                            onChange={(e) => {
                                                setSearchRitual(!searchRitual);
                                            }}
                                            disabled={saving}
                                        />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row className='g-3'>
                    {/* Focused Card Editor */}
                    <Col lg={6}>
                        <Card className={`h-100 shadow-sm}`}>
                            <Card.Header as='h5' style={{ backgroundColor: `${SCHOOL_COLORS[focusedCardData.school]}` }}>
                                {focusedCardId ? `Edit Card: ${focusedCardData.name}` : 'No Card Selected'}
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col xs={5}>
                                        <ExpandToFitContainer>
                                            <PlayingCard
                                                name={focusedCardData.name}
                                                energizeAmount={focusedCardData.energize_amount}
                                                focus={focusedCardData.cost}
                                                cost={focusedCardData.cost}
                                                castThreshold={focusedCardData.cast_threshold}
                                                school={focusedCardData.school}
                                                ritual={isEffectType("ritual", focusedCardData.play_ability ? JSON.parse(focusedCardData.play_ability) : null)
                                                    || isEffectType("ritual", focusedCardData.cast_ability ? JSON.parse(focusedCardData.cast_ability) : null)
                                                    || isEffectType("ritual", focusedCardData.sustain_ability ? JSON.parse(focusedCardData.sustain_ability) : null)
                                                    || isEffectType("ritual", focusedCardData.passive_ability ? JSON.parse(focusedCardData.passive_ability) : null)
                                                }
                                                classCard={focusedCardData.class_card}
                                                play={createText(parseEffect(focusedCardData.play_ability as string))}
                                                cast={createText(parseEffect(focusedCardData.cast_ability as string))}
                                                sustain={createText(parseEffect(focusedCardData.sustain_ability as string))}
                                                passive={createText(parseEffect(focusedCardData.passive_ability as string))}
                                                scaleOverride={1}
                                            />
                                        </ExpandToFitContainer>
                                    </Col>
                                    {/* Basic Card Details */}
                                    <Col xs={7}>
                                        <Row className='mb-3 g-2'>
                                            <Col xs={3} className='text-end fw-semibold pt-2'>({focusedCardData.card_id ? focusedCardData.card_id : "##"})Name:</Col>
                                            <Col xs={9}>
                                                <Form.Control
                                                    value={focusedCardData.name}
                                                    onChange={(e) => dispatchFocusedCardData({ name: e.target.value })}
                                                    disabled={saving}
                                                />
                                            </Col>
                                        </Row>
                                        <Row className='mb-3 g-2'>
                                            <Col xs={3} className='text-end fw-semibold pt-2'>Set:</Col>
                                            <Col xs={9}>
                                                <Form.Control
                                                    value={focusedCardData.set}
                                                    onChange={(e) => dispatchFocusedCardData({ set: e.target.value })}
                                                    disabled={saving}
                                                />
                                            </Col>
                                        </Row>
                                        <Row className='mb-3 g-2'>
                                            <Col xs={3} className='text-end fw-semibold pt-2'>Max Ct:</Col>
                                            <Col xs={9}>
                                                <Form.Control
                                                    type='number'
                                                    value={focusedCardData.max_count}
                                                    onChange={(e) => dispatchFocusedCardData({ max_count: Number(e.target.value) })}
                                                    disabled={saving}
                                                />
                                            </Col>
                                        </Row>
                                        <Row className='mb-3 g-2'>
                                            <Col xs={3} className='text-end fw-semibold pt-2'>School:</Col>
                                            <Col xs={9}>
                                                <Dropdown onSelect={(val: string | null) => dispatchFocusedCardData({ max_count: val === "Manifest" ? 0 : 4, school: SCHOOL_NAMES.find(sn => sn === val) ?? "Manifest" })}>
                                                    <Dropdown.Toggle
                                                        variant='outline-secondary'
                                                        className='w-100 text-start'
                                                        disabled={saving}
                                                    >
                                                        {focusedCardData.school || 'Select School'}
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu className='w-100'>
                                                        {SCHOOL_NAMES.map((school) => (
                                                            <Dropdown.Item key={school} eventKey={school}>
                                                                {school}
                                                            </Dropdown.Item>
                                                        ))}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </Col>
                                        </Row>
                                        <Row className='mb-3 g-2'>
                                            <Col xs={3} className='text-end fw-semibold pt-2'>Cost:</Col>
                                            <Col xs={9}>
                                                <Form.Control
                                                    type='number'
                                                    value={focusedCardData.cost}
                                                    onChange={(e) => dispatchFocusedCardData({ cost: Number(e.target.value) })}
                                                    disabled={saving}
                                                />
                                            </Col>
                                        </Row>
                                        <Row className='mb-3 g-2'>
                                            <Col xs={3} className='text-end fw-semibold pt-2'>Cast:</Col>
                                            <Col xs={9}>
                                                <Form.Control
                                                    type='number'
                                                    value={focusedCardData.cast_threshold}
                                                    onChange={(e) => dispatchFocusedCardData({ cast_threshold: Number(e.target.value) })}
                                                    disabled={saving}
                                                />
                                            </Col>
                                        </Row>
                                        <Row className='mb-3 g-2'>
                                            <Col xs={3} className='text-end fw-semibold pt-2'>Class:</Col>
                                            <Col xs={9}>
                                                <Form.Check
                                                    style={{ marginTop: "10px" }}
                                                    checked={focusedCardData.class_card}
                                                    onChange={(e) => {
                                                        if (!focusedCardData.class_card)
                                                            dispatchFocusedCardData({ cost: 10, energize_amount: 3, cast_threshold: 0, max_count: 1 });
                                                        dispatchFocusedCardData({ class_card: !focusedCardData.class_card })
                                                    }}
                                                    disabled={saving}
                                                />
                                            </Col>
                                        </Row>
                                        {focusedCardData.class_card && <Row >
                                            <Col xs={3} className='text-end fw-semibold pt-2'>Energy:</Col>
                                            <Col xs={9}>
                                                <Form.Control
                                                    type='number'
                                                    value={focusedCardData.energize_amount}
                                                    onChange={(e) => dispatchFocusedCardData({ energize_amount: Number.parseInt(e.target.value) })}
                                                    disabled={saving}
                                                />
                                            </Col>
                                        </Row>}
                                    </Col>
                                </Row>

                                {focusedCardId === null ? (
                                    <Alert variant='info' className='mb-0'>Select a card from the list to edit its details.</Alert>
                                ) : (
                                    <Form onSubmit={(e) => { e.preventDefault(); updateCard(); }}>
                                        <Row className='mb-3 align-items-center g-2'>
                                            <Col xs={4}>
                                                <Button
                                                    variant='primary'
                                                    type='submit'
                                                    className='w-100'
                                                    disabled={saving || focusedCardId === null}
                                                >
                                                    {saving ? 'Saving...' : 'Update Card'}
                                                </Button>
                                            </Col>
                                            <Col xs={4}>
                                                <Button
                                                    variant='outline-danger'
                                                    className='w-100'
                                                    onClick={deleteCard}
                                                    disabled={saving || focusedCardId === null}
                                                >
                                                    Delete Card
                                                </Button>
                                            </Col>
                                            <Col xs={4}>
                                                <Button
                                                    variant='outline-secondary'
                                                    className='w-100'
                                                    onClick={() => setFocusedCardId(null)} // Clear focus
                                                    disabled={saving || focusedCardId === null}
                                                >
                                                    Clear Selection
                                                </Button>
                                            </Col>
                                        </Row>

                                        <CardAbilitiesEditor
                                            focusedCardData={focusedCardData}
                                            dispatchFocusedCardData={dispatchFocusedCardData}
                                            saving={saving}
                                            abilityFields={ABILITY_FIELDS}
                                        />
                                    </Form>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Card List Display */}
                    <Col lg={6}>
                        <Row>
                            <Card className="h-100 shadow-sm">
                                <Card.Header as="h5">All Cards</Card.Header>
                                <Card.Body className="p-0 d-flex flex-column">
                                    {filteredCardList.length === 0 ? (
                                        <Alert variant="info" className="m-3">
                                            No cards match your search criteria.
                                        </Alert>
                                    ) : (
                                        <>
                                            <ListGroup variant="flush" className="flex-grow-1">
                                                {paginatedCards.map((card) => (
                                                    <ListGroup.Item
                                                        key={card.card_id}
                                                        action
                                                        active={card.card_id === focusedCardId}
                                                        onClick={() => setFocusedCardId(card.card_id)}
                                                        className="d-flex align-items-center py-2"
                                                        style={{
                                                            color: `${SCHOOL_COLORS[card.school]}`,
                                                            backgroundColor: `#333`,
                                                        }}
                                                    >
                                                        <Col xs={1} className="fw-bold">
                                                            {card.card_id}
                                                        </Col>
                                                        <Col xs={1} className="fw-bold">
                                                            {card.class_card ? "*" : ""}
                                                        </Col>
                                                        <Col xs={3}>{card.name}</Col>
                                                        <Col xs={7} className="text-end">
                                                            Cost: {card.cost} / Cast: {card.cast_threshold} {card.energize_amount && card.class_card ? `/ Energize: ${card.energize_amount}` : ''}
                                                        </Col>
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>

                                            {totalPages > 1 && (
                                                <div className="d-flex justify-content-center align-items-center gap-2 p-3 border-top">
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                                        disabled={currentPage === 1}
                                                    >
                                                        Previous
                                                    </Button>
                                                    <span>
                                                        Page {currentPage} of {totalPages}
                                                    </span>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                                        disabled={currentPage === totalPages}
                                                    >
                                                        Next
                                                    </Button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Row>
                    </Col>
                </Row>
            </Card.Body >
        </Card >
    );
}
